import { HttpClient } from "@angular/common/http";
import { isPlatformServer } from "@angular/common";
import { Injectable, PLATFORM_ID, inject } from "@angular/core";

import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from "rxjs";

import { environment } from "../../../../public/environments/environment";
import { IWishlistModel } from "../interface/wishlist.interface";
import { IApiResponse } from "../interface/api-response.interface";
import { BaseUrlEnum, getBaseURL } from "./common/base-url-resolver.util";
import { MasterService } from "./common/master.service";
import { AuthState } from "../state/auth.state";
import { Store } from "@ngxs/store";

import { NewAccountState } from "../state/account.new.state";
import { _IProduct } from "../interface/product.interface";
import { NotificationService } from "./notification.service";

@Injectable({
  providedIn: "root",
})
export class WishlistService {
  private wishlistItemsSubject = new BehaviorSubject<any[]>([]);
  wishlistItems$ = this.wishlistItemsSubject.asObservable();

  private wishlistCountSubject = new BehaviorSubject<number>(0);
  wishlistCount$ = this.wishlistCountSubject.asObservable();

  private store = inject(Store);
  private http = inject(HttpClient);
  private masterService = inject(MasterService);
  private platformId = inject<Object>(PLATFORM_ID);
  public skeletonLoader: boolean = false;
  private notificationService = inject(NotificationService);

  isAuthenticated$: Observable<Boolean> = inject(Store).select(
    AuthState.isAuthenticated
  );
  user$: Observable<any> = inject(Store).select(
    NewAccountState.user
  ) as Observable<any>;

  private createGuestWishlistResponse(message: string): IApiResponse {
    return {
      success: true,
      data: { cartItems: [] },
      message,
      errors: [],
      statusCode: 200,
    };
  }

  getWishlistItems(): Observable<IWishlistModel> {
    return this.http.get<IWishlistModel>(`${environment.URL}/wishlist.json`);
  }

  // ==================================Wishlist Start=============================
  loadWishlistItems(): Observable<IApiResponse> {
    return this.getWishlistByCustomerId().pipe(
      tap((res: IApiResponse) => {
        console.log(res);

        const items = (res.data?.cartItems || []).map((ci: any) => ({
          id: Number(ci.id),
          name: ci.productName,
          image: ci.productImagePath,
          price: ci.unitPrice,
          qty: ci.qty,
          slug: ci.slug,
          isInStock: ci.isInStock,
          productVariantId: ci.productVariantId,
          cartInfoId: ci.cartInfoId,
        }));

        items.sort((a: any, b: any) => Number(a.id) - Number(b.id));

        // update state
        this.wishlistItemsSubject.next(items);
        this.wishlistCountSubject.next(items.length);
      }),
      catchError((err) => {
        console.error("Failed to load cart:", err);
        return throwError(() => err);
      })
    );
  }

  getWishlistByCustomerId(): Observable<IApiResponse> {
    const user = this.store.selectSnapshot(NewAccountState.user);

    if (!user?.id) {
      if (!isPlatformServer(this.platformId)) {
        this.notificationService.showError(
          "Please log in to Add your wishlist items."
        );
      }
      return of(this.createGuestWishlistResponse("Guest wishlist: no authenticated customer."));
    }
    return this.masterService.get<IApiResponse>(
      getBaseURL(this.platformId, BaseUrlEnum.Ordering),
      `Order/ShoppingCart/customer/wishlist-by-customerid/${user.id}`
    );
  }

  AddToWishlist(product: _IProduct): Observable<any> {
    const user = this.store.selectSnapshot(NewAccountState.user);
    if (!user?.id) {
      return of(null);
    }

    return this.getWishlistByCustomerId().pipe(
      catchError((err) => {
        if (err.status === 404) {
          const user = this.store.selectSnapshot(NewAccountState.user);
          if (!user?.id) {
            return throwError(
              () => new Error("No customer info found in localStorage")
            );
          }

          const newCart: any = {
            cartType: 1,
            customerInfoId: user.id,
            cartItems: [
              {
                cartInfoId: 0,
                productVariantId: product.productVariantGuidId,
                qty: 1,
              },
            ],
          };

          return this.masterService.post<IApiResponse>(
            getBaseURL(this.platformId, BaseUrlEnum.Ordering),
            `Order/ShoppingCart`,
            newCart
          );
        }
        return throwError(() => err);
      }),
      switchMap((res: IApiResponse) => {
        if (res?.data?.id) {
          return this.addWishlistItem(res.data.id, product); // ✅ chained
        }
        return of(null);
      })
    );
  }

  private addWishlistItem(cartId: number, product: any): Observable<any> {
    return this.masterService
      .get<IApiResponse>(
        getBaseURL(this.platformId, BaseUrlEnum.Ordering),
        `Order/ShoppingCart/${cartId}`
      )
      .pipe(
        switchMap((res) => {
          const existingItem = res.data.cartItems.find(
            (i: any) => i.productVariantId === product.productVariantGuidId
          );

          if (existingItem) {
            this.notificationService.showError("Already in Wishlist");
            return of(null);
          }

          const newItem = {
            cartInfoId: cartId,
            productVariantId: product.productVariantGuidId,
            qty: 1,
          };

          return this.masterService.post<IApiResponse>(
            getBaseURL(this.platformId, BaseUrlEnum.Ordering),
            `Order/ShoppingCart/cart-item`,
            newItem
          );
        }),
        tap(() => {
          this.notificationService.showSuccess("Product added to Wishlist");
        })
      );
  }

  // private addWishlistItem(cartId: number, product: any) {
  //    this.masterService
  //     .get<IApiResponse>(
  //       getBaseURL(this.platformId, BaseUrlEnum.Ordering),
  //       `Order/ShoppingCart/${cartId}`
  //     )
  //     .subscribe({
  //       next: (res) => {
  //         const existingItem = res.data.cartItems.find(
  //           (i: any) => i.productVariantId === product.productVariantGuidId
  //         );
  //         if (existingItem) {
  //           // Swal.fire({
  //           //   toast: true,
  //           //   position: "top-end",
  //           //   icon: "info",
  //           //   title: "Already in Wishlist",
  //           //   showConfirmButton: false,
  //           //   timer: 2000,
  //           // });
  //           this.notificationService.showError("Already in Wishlist");
  //         } else {
  //           const newItem: any = {
  //             cartInfoId: cartId,
  //             productVariantId: product.productVariantGuidId,
  //             qty: 1,
  //           };
  //           this.masterService
  //             .post<IApiResponse>(
  //               getBaseURL(this.platformId, BaseUrlEnum.Ordering),
  //               `Order/ShoppingCart/cart-item`,
  //               newItem
  //             )
  //             .subscribe({
  //               next: (res) => {
  //                 // Swal.fire({
  //                 //   toast: true,
  //                 //   position: "top-end",
  //                 //   icon: "success",
  //                 //   title: "Product added to Wishlist",
  //                 //   showConfirmButton: false,
  //                 //   timer: 2000,
  //                 // });
  //                 // alert();
  //                 this.notificationService.showSuccess(
  //                   "Product added to Wishlist"
  //                 );
  //                 this.loadWishlistItems();
  //               },
  //               error: (err) =>
  //                 console.error(" Failed to add item to Wishlist:", err),
  //             });
  //         }
  //       },
  //     });
  // }

  removeWishlistItem(itemId: number): Observable<IApiResponse> {
    const isAuthenticated = this.store.selectSnapshot(
      AuthState.isAuthenticated
    );

    if (!isAuthenticated) {
      this.notificationService.showError("please login first!");
      return throwError(() => new Error("Not authenticated"));
    }

    return this.deleteWishlistItem(itemId);
  }

  deleteWishlistItem(itemId: number): Observable<IApiResponse> {
    return this.masterService.delete<IApiResponse>(
      getBaseURL(this.platformId, BaseUrlEnum.Ordering),
      `Order/ShoppingCart/cart-item/${itemId}` //
    );
  }
  // ==================================Wishlist End=============================
}
